import { type Ref, type StyleValue, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'

export type Position = 'top' | 'bottom' | 'left' | 'right'

const ARROW_OFFSET = 6

// we want to have a gap between the tooltip and the window edge
const WINDOW_EDGE_PADDING = 10

export const useTooltip = ({
    trigger,
    hideDelayMs,
    position,
    additionalOffset,
    tooltip,
}: {
    trigger: Ref<HTMLElement | null>
    hideDelayMs: number
    position: Position
    additionalOffset: number
    tooltip: Ref<HTMLElement | null>
}) => {
    const tooltipStyle = shallowRef<StyleValue>({})
    const isVisible = ref(false)
    const showTimeout = ref<number | null>(null)
    const adjustedPosition = ref<Position>(position)
    const updatePositionAndStyle = () => {
        const triggerElement = trigger.value?.firstElementChild
        if (!tooltip.value || !triggerElement) {
            return
        }

        const tooltipRect = tooltip.value.getBoundingClientRect()
        const triggerRect = triggerElement.getBoundingClientRect()

        // Get window dimensions and scroll position
        const windowHeight = window.innerHeight
        const windowWidth = window.innerWidth

        // Reset to original position
        adjustedPosition.value = position
        const space = {
            top: triggerRect.top, // Space from top of viewport
            bottom: windowHeight - triggerRect.bottom, // Space from bottom of viewport
            left: triggerRect.left, // Space from left of viewport
            right: windowWidth - triggerRect.right, // Space from right of viewport
        }

        const offset = additionalOffset + ARROW_OFFSET + WINDOW_EDGE_PADDING

        // Check if there's enough space in the preferred position
        switch (position) {
            case 'top':
                if (space.top < tooltipRect.height + offset) {
                    adjustedPosition.value = 'bottom'
                }
                break
            case 'bottom':
                if (space.bottom < tooltipRect.height + offset) {
                    adjustedPosition.value = 'top'
                }
                break
            case 'left':
                if (space.left < tooltipRect.width + offset) {
                    adjustedPosition.value = 'right'
                }
                break
            case 'right':
                if (space.right < tooltipRect.width + offset) {
                    adjustedPosition.value = 'left'
                }
                break
        }

        // Calculate position based on adjusted position
        let top = 0
        let left = 0
        let transform = ''
        const basicOffset = additionalOffset + ARROW_OFFSET

        switch (adjustedPosition.value) {
            case 'top':
                top = triggerRect.top - basicOffset
                left = triggerRect.left + triggerRect.width / 2
                transform = 'translate(-50%, -100%)'
                break
            case 'bottom':
                top = triggerRect.bottom + basicOffset
                left = triggerRect.left + triggerRect.width / 2
                transform = 'translate(-50%, 0)'
                break
            case 'left':
                top = triggerRect.top + triggerRect.height / 2
                left = triggerRect.left - basicOffset
                transform = 'translate(-100%, -50%)'
                break
            case 'right':
                top = triggerRect.top + triggerRect.height / 2
                left = triggerRect.right + basicOffset
                transform = 'translate(0, -50%)'
                break
        }

        // Adjust for viewport boundaries to prevent clipping
        if (adjustedPosition.value === 'top' || adjustedPosition.value === 'bottom') {
            // Horizontal boundary check
            const finalLeft = left - tooltipRect.width / 2
            if (finalLeft < WINDOW_EDGE_PADDING) {
                left += WINDOW_EDGE_PADDING - finalLeft
            } else if (finalLeft + tooltipRect.width > windowWidth - WINDOW_EDGE_PADDING) {
                left -= finalLeft + tooltipRect.width - (windowWidth - WINDOW_EDGE_PADDING)
            }
        } else {
            // Vertical boundary check
            const finalTop = top - tooltipRect.height / 2
            if (finalTop < WINDOW_EDGE_PADDING) {
                top += WINDOW_EDGE_PADDING - finalTop
            } else if (finalTop + tooltipRect.height > windowHeight - WINDOW_EDGE_PADDING) {
                top -= finalTop + tooltipRect.height - (windowHeight - WINDOW_EDGE_PADDING)
            }
        }

        tooltipStyle.value = {
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            transform,
        }
    }
    const hideTooltip = () => {
        if (showTimeout.value) {
            clearTimeout(showTimeout.value)
            showTimeout.value = null
        }
        showTimeout.value = window.setTimeout(() => {
            isVisible.value = false
        }, hideDelayMs)
    }

    const hideTooltipOnScroll = () => {
        if (showTimeout.value) {
            clearTimeout(showTimeout.value)
            showTimeout.value = null
        }
        isVisible.value = false
    }

    const showTooltip = () => {
        if (showTimeout.value) {
            clearTimeout(showTimeout.value)
        }
        // we want to have a delay before the tooltip is shown
        showTimeout.value = window.setTimeout(() => {
            isVisible.value = true
        }, 100)
    }

    const onHoverTooltip = () => {
        isVisible.value = true
        if (showTimeout.value) {
            clearTimeout(showTimeout.value)
        }
    }

    const handleScroll = () => {
        hideTooltipOnScroll()
    }

    onMounted(() => {
        window.addEventListener('resize', updatePositionAndStyle)
        window.addEventListener('scroll', handleScroll, true) // true for capture phase to handle all scrollable parents
    })

    onBeforeUnmount(() => {
        if (showTimeout.value) {
            clearTimeout(showTimeout.value)
        }
        window.removeEventListener('resize', updatePositionAndStyle)
        window.removeEventListener('scroll', handleScroll, true)
    })

    watch(isVisible, (visible: boolean) => {
        if (visible) {
            // Wait for tooltip to render
            nextTick(updatePositionAndStyle)
        }
    })
    return {
        hideTooltip,
        showTooltip,
        onHoverTooltip,
        tooltipStyle,
        isVisible,
        adjustedPosition,
    }
}
